import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { createBrowserFhVitalsClient } from './browser-fh-vitals-client'
import type { FhVitalsClient } from './fh-vitals-types'

const FhVitalsClientContext = createContext<FhVitalsClient | null>(null)

/**
 * The substitution point the task brief asks for: "the SDK module needs a seam you can substitute
 * in tests — design for that from the start rather than retrofitting." Production renders with no
 * `client` prop and gets the real browser SDK; every unit and component test renders with a fake
 * FhVitalsClient here instead, so no test — this one included — ever loads fhvitals_sdk.js or
 * reaches wss://hcs-va.faceheart.com.
 */
export function FhVitalsClientProvider({
  client,
  children,
}: {
  client?: FhVitalsClient
  children: ReactNode
}) {
  const value = useMemo(() => client ?? createBrowserFhVitalsClient(), [client])
  return <FhVitalsClientContext.Provider value={value}>{children}</FhVitalsClientContext.Provider>
}

export function useFhVitalsClient(): FhVitalsClient {
  const client = useContext(FhVitalsClientContext)
  if (!client) {
    throw new Error('useFhVitalsClient() called outside an FhVitalsClientProvider')
  }
  return client
}
